'use client';

import React, { useEffect, useState } from 'react';
import { AuthModal } from '@/components/auth-modal';
import { Button } from '@/components/button/animatedButton';
import DMDialog from '@/components/dialog';
import { SkeletonLoader } from '@/components/skeleton-loader';
import useOneOnOneStore from '@/lib/zustand/store/oneOnOneStore';
import NumberFlow from '@number-flow/react';
import {
  ArrowRightIcon,
  GraduationCap,
  HeartHandshake,
  HeartHandshakeIcon,
  User,
} from 'lucide-react';
import { useSession } from 'next-auth/react';
import { createPortal } from 'react-dom';
import OneOnOne from './OneOnOne';

export type DebugMediaItem = {
  title: string;
  description: string;
  duration?: string;
  price: number;
  icon?: React.ElementType;
  setSelectedItem?: React.Dispatch<React.SetStateAction<DebugMediaItem | null>>;
};

export const iconMap: { [key: string]: React.ElementType } = {
  GraduationCap,
  HeartHandshake,
  User,
  HeartHandshakeIcon,
};
const OneOnOneList: React.FC = () => {
  const [oneOnOneList, setOneOnOneList] = useState<DebugMediaItem[] | null>(null);

  const { setModal, setSelectedItem, selectedItem } = useOneOnOneStore();
  const { data } = useSession();

  const openModal = (item: DebugMediaItem) => {
    if (!data) return;
    setSelectedItem(item);
  };

  const closeModal = () => {
    setModal('closed');
    setSelectedItem(null);
  };

  useEffect(() => {
    fetchOneOnOneData().then(data => {
      setOneOnOneList(data);
    });
  }, []);

  const fetchOneOnOneData = async () => {
    const response = await fetch('/api/one-on-one/getAll');
    if (!response.ok) {
      throw new Error('Failed to fetch data');
    }
    return response.json();
  };

  return (
    <div className='container !pb-28'>
      <div className='text-center'>
        <h2 className='leading-10 max-sm:text-xl'>Book a 1:1 with Debug Media</h2>
        <p className='text-sm max-w-3xl mt-4 sm:mt-8 mx-auto'>
          Unlock your potential with a personalized 1:1 session. Whether you're seeking expert
          guidance, advice, or support, our tailored approach will help you achieve your goals and
          take the next step in your journey.
        </p>
      </div>
      <div className='flex flex-wrap gap-6 gap-x-10 mt-12 justify-center'>
        {oneOnOneList
          ? oneOnOneList?.map((item: any, index) => {
              const IconComponent = iconMap[item.icon];
              return (
                <div
                  key={index}
                  className='bg-brand-secondary p-4 rounded-2xl flex justify-between flex-col max-w-[500px] w-full'
                >
                  <div className='flex flex-col'>
                    <span className='text-lg sm:text-xl font-bold mb-2'>{item.title}</span>
                    <span className='text-xs sm:text-sm text-gray-300 leading-5 text-start mb-6'>
                      {item?.description}
                    </span>
                  </div>
                  <div className='flex items-center justify-between bg-brand-primary-border p-4 rounded-xl'>
                    <div className='flex items-center space-x-4'>
                      <div className='rounded-lg w-12 h-12 flex items-center justify-center'>
                        {IconComponent && <IconComponent className='w-full h-full text-white' />}
                      </div>
                      <div className='flex flex-col'>
                        <span className='text-gray-400 font-medium'>{item.duration}</span>
                        <span className='text-gray-300 text-xs'>Google Meet</span>
                      </div>
                    </div>
                    <AuthModal>
                      <Button
                        onClick={() => openModal(item)}
                        variant='expandIcon'
                        Icon={ArrowRightIcon}
                        iconPlacement='right'
                        className='text-brand-primary  font-medium rounded-full border border-brand-primary flex items-center bg-transparent hover:bg-transparent cursor-pointer overflow-clip'
                      >
                        <NumberFlow
                          className='text-semibold font-medium text-slate-300 '
                          value={item.price}
                          format={{
                            style: 'currency',
                            currency: 'INR',
                            trailingZeroDisplay: 'stripIfInteger',
                          }}
                        />
                        {/* ₹ {item.price} */}
                      </Button>
                    </AuthModal>
                  </div>
                </div>
              );
            })
          : Array(4)
              .fill(null)
              .map((_: any, index: any) => <SkeletonLoader key={index} height={150} width={450} />)}

        {selectedItem &&
          createPortal(
            <DMDialog
              outSideClickClose={false}
              open={!!selectedItem}
              setOpen={closeModal}
              className='w-full max-w-[90%] lg:max-w-[70%] xl:max-w-[60%] max-h-[95%] overflow-y-auto'
              content={
                <div className='mb-2 p-4 sm:p-8'>
                  <OneOnOne {...selectedItem} />
                </div>
              }
            />,
            document.body
          )}
      </div>
    </div>
  );
};

export default OneOnOneList;
